import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { AiFilterChip } from '~/components/AiSearchChips/AiFilterChip/AiFilterChip';
import { dateRangeHandler, nameSelectionHandler } from '~/components/AiSearchChips/AiFilterChip/chipHandlers';
import { isExactMatch, rangeDateValueLabel } from '~/components/AiSearchChips/DateRangeChip/DateRangeChip.utils';
import { currentValue } from '~/components/AiSearchChips/StringMatchChip/StringMatchChip.utils';
import { ALLOWED_DRAFTS_AI_SEARCH_STATUS } from '~/types/api/drafts-search';
import { hasSettingsChips } from '../../settings-predicates';
import { selectedMode, togglePredicate } from '../EffectiveDateModeChip/EffectiveDateModeChip.utils';
import { editableValue, isEmptyPredicate, isExcludeMode, isRangeModeOnly, multiValuesFor, parseNumbers, predicateFromNumbers, valueLabelFor } from '../intRangeChip.utils';
import { currentValue as modifiedByValue, isExcludeMode as modifiedByIsExclude, valueLabelFor as modifiedByLabel } from '../ModifiedByChip/ModifiedByChip.utils';
import { presentQuantifiers } from '../ServicesChips/service-predicate';
import { ServicesChips } from '../ServicesChips/ServicesChips';
import { SettingsChips } from '../SettingsChips/SettingsChips';
import { localizeStatus } from '../StatusChip/StatusChip.utils';
const setToCheckboxes = (predicate)=>{
    var _predicate_in;
    return predicate.nin != null ? {
        selected: predicate.nin,
        operator: 'exclude'
    } : {
        selected: (_predicate_in = predicate.in) !== null && _predicate_in !== void 0 ? _predicate_in : [],
        operator: 'include'
    };
};
const checkboxesToSet = (next)=>next.operator === 'exclude' ? {
        nin: [
            ...next.selected
        ]
    } : {
        in: [
            ...next.selected
        ]
    };
const joinedLabel = (items, selected)=>selected.length === 0 ? undefined : selected.map((value)=>{
        var _ref;
        var _items_find;
        return (_ref = (_items_find = items.find((item)=>item.value === value)) === null || _items_find === void 0 ? void 0 : _items_find.label) !== null && _ref !== void 0 ? _ref : value;
    }).join(', ');
export const buildDraftsPredicateChipTexts = (t, commonFilter, agr)=>({
        labels: {
            agreementName: t.agreementNameLabel(),
            clientName: t.clientNameLabel(),
            contactName: t.recipientNameLabel(),
            status: t.statusLabel(),
            effectiveDate: t.effectiveDateLabel(),
            lastUpdated: t.lastUpdatedLabel()
        },
        statusOptions: ALLOWED_DRAFTS_AI_SEARCH_STATUS.map((status)=>({
                value: status,
                label: localizeStatus(status, t)
            })),
        dateRange: {
            allTime: agr.dateRangeLabels.allTime(),
            specificRange: agr.dateRangeLabels.specificRange(),
            from: agr.dateRangeLabels.from(),
            to: agr.dateRangeLabels.to()
        },
        clientPresence: {
            label: t.clientPresenceFieldLabel(),
            any: t.clientPresenceAny(),
            none: t.clientPresenceNone()
        },
        reviewers: {
            label: t.reviewersFieldLabel(),
            any: t.reviewersAny(),
            none: t.reviewersNone()
        },
        effectiveDateMode: {
            label: t.effectiveDateModeLabel(),
            onAcceptance: t.effectiveDateModeOnAcceptance(),
            specificDate: t.effectiveDateModeSpecificDate()
        },
        modifiedBy: {
            label: t.modifiedByFieldLabel(),
            me: t.modifiedByValueMe()
        },
        apply: t.applyButton(),
        all: t.chipValueAll(),
        selectAll: commonFilter.selectAll(),
        orConnector: t.orConnector(),
        netDays: {
            label: t.netDaysLabel(),
            suffix: t.netDaysSuffix(),
            validationError: t.netDaysValidationError(),
            valueIs: (value)=>t.netDaysValueIs({
                    value
                }),
            valueOver: (value)=>t.netDaysValueOver({
                    value
                }),
            valueUnder: (value)=>t.netDaysValueUnder({
                    value
                })
        }
    });
export const buildPredicateChipUnits = (param)=>{
    let { predicate, testIds, callbacks, clientNameOptions, contactNameOptions, agreementNameOptions, texts, settingsTone } = param;
    var _predicate_contactId, _predicate_paymentSettings;
    const nameRadioTexts = {
        apply: texts.apply,
        all: texts.all
    };
    const showServices = predicate.services != null && presentQuantifiers(predicate.services).length > 0;
    const units = [];
    if (predicate.agreementName != null) {
        const agreementName = predicate.agreementName;
        const current = currentValue(agreementName);
        units.push({
            key: 'agreementName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.AgreementName,
                fieldLabel: texts.labels.agreementName,
                items: agreementNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: agreementName,
                    onChange: callbacks.onAgreementNameChange
                }),
                onRemove: ()=>callbacks.onAgreementNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_agreement_name",
                texts: nameRadioTexts
            })
        });
    }
    if (predicate.clientName != null) {
        const clientName = predicate.clientName;
        const current = currentValue(clientName);
        units.push({
            key: 'clientName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ClientName,
                fieldLabel: texts.labels.clientName,
                items: clientNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: clientName,
                    onChange: callbacks.onClientNameChange
                }),
                onRemove: ()=>callbacks.onClientNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_client_name",
                texts: nameRadioTexts
            })
        });
    }
    if (((_predicate_contactId = predicate.contactId) === null || _predicate_contactId === void 0 ? void 0 : _predicate_contactId.exists) != null) {
        const contactId = predicate.contactId;
        units.push({
            key: 'contactId',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: testIds.ClientPresence,
                fieldLabel: texts.clientPresence.label,
                valueLabel: contactId.exists ? texts.clientPresence.any : texts.clientPresence.none,
                onToggle: ()=>callbacks.onContactIdChange(_object_spread_props(_object_spread({}, contactId), {
                        exists: contactId.exists !== true
                    })),
                onRemove: ()=>callbacks.onContactIdChange(undefined),
                biPrefix: "drafts_ai_search_chip_client_presence"
            })
        });
    }
    if (predicate.reviewers != null) {
        const reviewers = predicate.reviewers;
        units.push({
            key: 'reviewers',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: testIds.Reviewers,
                fieldLabel: texts.reviewers.label,
                valueLabel: reviewers.exists ? texts.reviewers.any : texts.reviewers.none,
                onToggle: ()=>callbacks.onReviewersChange({
                        exists: !reviewers.exists
                    }),
                onRemove: ()=>callbacks.onReviewersChange(undefined),
                biPrefix: "drafts_ai_search_chip_reviewers"
            })
        });
    }
    if (predicate.contactName != null) {
        const contactName = predicate.contactName;
        const current = currentValue(contactName);
        units.push({
            key: 'contactName',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.ContactName,
                fieldLabel: texts.labels.contactName,
                items: contactNameOptions,
                selected: current !== '' ? current : null,
                onApply: nameSelectionHandler({
                    current,
                    predicate: contactName,
                    onChange: callbacks.onContactNameChange
                }),
                onRemove: ()=>callbacks.onContactNameChange(undefined),
                biPrefix: "drafts_ai_search_chip_contact_name",
                texts: nameRadioTexts
            })
        });
    }
    if (predicate.status != null) {
        const { selected, operator } = setToCheckboxes(predicate.status);
        const shownStatuses = operator === 'exclude' ? texts.statusOptions.map((option)=>option.value).filter((value)=>!selected.includes(value)) : selected;
        units.push({
            key: 'status',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "checkboxes",
                testId: testIds.Status,
                fieldLabel: texts.labels.status,
                items: texts.statusOptions,
                selected: selected,
                operator: operator,
                valueLabel: joinedLabel(texts.statusOptions, shownStatuses),
                onApply: (next)=>callbacks.onStatusChange(next.selected.length === 0 ? undefined : checkboxesToSet(next)),
                onRemove: ()=>callbacks.onStatusChange(undefined),
                biPrefix: "drafts_ai_search_chip_status"
            })
        });
    }
    if (predicate.effectiveDate != null) {
        const effectiveDate = predicate.effectiveDate;
        const exact = isExactMatch(effectiveDate);
        const isEmpty = effectiveDate.gte == null && effectiveDate.lte == null && effectiveDate.eq == null && effectiveDate.neq == null;
        units.push({
            key: 'effectiveDate',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.EffectiveDate,
                fieldLabel: texts.labels.effectiveDate,
                items: [
                    {
                        value: 'all',
                        label: texts.dateRange.allTime
                    },
                    {
                        value: 'range',
                        label: texts.dateRange.specificRange
                    }
                ],
                selected: effectiveDate.gte != null || effectiveDate.lte != null ? 'range' : 'all',
                valueLabel: isEmpty ? texts.all : rangeDateValueLabel(effectiveDate, texts.labels.effectiveDate),
                editable: !exact,
                datePicker: {
                    rangeValue: 'range',
                    from: effectiveDate.gte,
                    to: effectiveDate.lte,
                    texts: {
                        from: texts.dateRange.from,
                        to: texts.dateRange.to
                    }
                },
                onApply: dateRangeHandler(callbacks.onEffectiveDateChange),
                onRemove: ()=>callbacks.onEffectiveDateChange(undefined),
                biPrefix: "drafts_ai_search_chip_effective_date",
                tone: settingsTone,
                texts: {
                    apply: texts.apply,
                    all: texts.all
                }
            })
        });
    }
    if (predicate.effectiveDateMode != null) {
        const effectiveDateMode = predicate.effectiveDateMode;
        units.push({
            key: 'effectiveDateMode',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "toggle",
                testId: testIds.EffectiveDateMode,
                fieldLabel: texts.effectiveDateMode.label,
                valueLabel: selectedMode(effectiveDateMode) === 'specificDate' ? texts.effectiveDateMode.specificDate : texts.effectiveDateMode.onAcceptance,
                onToggle: ()=>callbacks.onEffectiveDateModeChange(togglePredicate(effectiveDateMode)),
                onRemove: ()=>callbacks.onEffectiveDateModeChange(undefined),
                biPrefix: "drafts_ai_search_chip_effective_date_mode"
            })
        });
    }
    if (predicate.lastUpdatedAt != null) {
        const lastUpdatedAt = predicate.lastUpdatedAt;
        const exact = isExactMatch(lastUpdatedAt);
        const isEmpty = lastUpdatedAt.gte == null && lastUpdatedAt.lte == null && lastUpdatedAt.eq == null && lastUpdatedAt.neq == null;
        units.push({
            key: 'lastUpdatedAt',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "radio",
                testId: testIds.LastUpdated,
                fieldLabel: texts.labels.lastUpdated,
                items: [
                    {
                        value: 'all',
                        label: texts.dateRange.allTime
                    },
                    {
                        value: 'range',
                        label: texts.dateRange.specificRange
                    }
                ],
                selected: lastUpdatedAt.gte != null || lastUpdatedAt.lte != null ? 'range' : 'all',
                valueLabel: isEmpty ? texts.all : rangeDateValueLabel(lastUpdatedAt, texts.labels.lastUpdated),
                editable: !exact,
                datePicker: {
                    rangeValue: 'range',
                    from: lastUpdatedAt.gte,
                    to: lastUpdatedAt.lte,
                    texts: {
                        from: texts.dateRange.from,
                        to: texts.dateRange.to
                    }
                },
                onApply: dateRangeHandler(callbacks.onLastUpdatedAtChange),
                onRemove: ()=>callbacks.onLastUpdatedAtChange(undefined),
                biPrefix: "drafts_ai_search_chip_last_updated",
                texts: {
                    apply: texts.apply,
                    all: texts.all
                }
            })
        });
    }
    if (predicate.lastModifiedBy != null) {
        var _lastModifiedBy_nin, _lastModifiedBy_in;
        const lastModifiedBy = predicate.lastModifiedBy;
        const isExclude = modifiedByIsExclude(lastModifiedBy);
        const values = isExclude ? (_lastModifiedBy_nin = lastModifiedBy.nin) !== null && _lastModifiedBy_nin !== void 0 ? _lastModifiedBy_nin : [] : (_lastModifiedBy_in = lastModifiedBy.in) !== null && _lastModifiedBy_in !== void 0 ? _lastModifiedBy_in : [];
        units.push({
            key: 'lastModifiedBy',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "text",
                testId: testIds.ModifiedBy,
                fieldLabel: texts.modifiedBy.label,
                value: modifiedByValue(lastModifiedBy),
                valueLabel: isExclude ? "≠".concat(modifiedByLabel(lastModifiedBy, texts.modifiedBy.me)) : modifiedByLabel(lastModifiedBy, texts.modifiedBy.me),
                editable: values.length <= 1,
                onApply: (input)=>callbacks.onLastModifiedByChange(input === '' ? undefined : isExclude ? {
                        nin: [
                            input
                        ]
                    } : {
                        in: [
                            input
                        ]
                    }),
                onRemove: ()=>callbacks.onLastModifiedByChange(undefined),
                biPrefix: "drafts_ai_search_chip_modified_by",
                texts: {
                    apply: texts.apply
                }
            })
        });
    }
    if (((_predicate_paymentSettings = predicate.paymentSettings) === null || _predicate_paymentSettings === void 0 ? void 0 : _predicate_paymentSettings.netTerms) != null) {
        const netTerms = predicate.paymentSettings.netTerms;
        const segments = multiValuesFor(netTerms);
        units.push({
            key: 'netTerms',
            node: /*#__PURE__*/ _jsx(AiFilterChip, {
                mode: "numberRange",
                testId: testIds.NetDays,
                fieldLabel: isEmptyPredicate(netTerms) ? '' : texts.netDays.label,
                value: editableValue(netTerms),
                valueLabel: valueLabelFor(netTerms, texts.netDays),
                valueSegments: segments === null || segments === void 0 ? void 0 : segments.map((segment)=>({
                        label: texts.netDays.valueIs(segment),
                        biName: 'drafts_ai_search_chip_edit_net_days_segment'
                    })),
                combinator: texts.orConnector,
                suffix: texts.netDays.suffix,
                tone: settingsTone,
                editable: !isRangeModeOnly(netTerms),
                validate: (input)=>parseNumbers(input) != null,
                onApply: (input)=>{
                    var _parseNumbers;
                    return callbacks.onNetTermsChange(predicateFromNumbers((_parseNumbers = parseNumbers(input)) !== null && _parseNumbers !== void 0 ? _parseNumbers : [], isExcludeMode(netTerms)));
                },
                onRemove: ()=>callbacks.onNetTermsChange(undefined),
                biPrefix: "drafts_ai_search_chip_net_days",
                texts: {
                    apply: texts.apply,
                    validationError: texts.netDays.validationError
                }
            })
        });
    }
    if (hasSettingsChips(predicate.paymentSettings, predicate.agreementSettings)) {
        units.push({
            key: 'settings',
            node: /*#__PURE__*/ _jsx(SettingsChips, {
                testId: testIds.Settings,
                paymentSettings: predicate.paymentSettings,
                agreementSettings: predicate.agreementSettings,
                onPaymentSettingsChange: callbacks.onPaymentSettingsChange,
                onAgreementSettingsChange: callbacks.onAgreementSettingsChange
            })
        });
    }
    if (showServices && predicate.services != null) {
        units.push({
            key: 'services',
            node: /*#__PURE__*/ _jsx(ServicesChips, {
                testId: testIds.Services,
                services: predicate.services,
                onChange: callbacks.onServicesChange
            })
        });
    }
    return units;
};
